import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconColumns, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { AppConstants } from "utils/constants";

// Column id as the table knows it (columnHelper.accessor sets accessorKey).
export const columnId = (col) => col.id ?? col.accessorKey;

// Only columns that set meta.show can be toggled; the rest are always shown.
export const isHideable = (col) => typeof col.meta?.show === "boolean";

// Default visibility from each column's meta.show, with the user's saved
// choices (id -> boolean) on top. Saved ids no longer in the list, or for
// columns without meta.show, are ignored.
export const getColumnVisibility = (columns = [], saved) =>
  Object.fromEntries(
    columns.map((col) => {
      const id = columnId(col);
      if (!isHideable(col)) return [id, true];
      const value = saved?.[id];
      return [id, typeof value === "boolean" ? value : col.meta.show];
    })
  );

/**
 * Popover of checkboxes for showing/hiding the list's columns (those with
 * meta.show).
 * visibility is id -> boolean; onChange gets the full new map, onReset goes
 * back to the column defaults. At least one column always stays visible.
 */
const ColumnSelectPopover = ({ columns: allColumns = [], visibility = {}, onChange, onReset }) => {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");

  // Columns without meta.show are always shown, so they aren't listed.
  const columns = allColumns.filter(isHideable);
  const hasFixedColumns = columns.length < allColumns.length;
  const visibleCount = columns.filter((col) => visibility[columnId(col)]).length;
  const term = search.trim().toLowerCase();
  const shown = columns.filter(
    (col) => !term || String(col.header ?? columnId(col)).toLowerCase().includes(term)
  );

  const setAll = (value) =>
    onChange(
      Object.fromEntries(
        columns.map((col, idx) => [columnId(col), value || (!hasFixedColumns && idx === 0)])
      )
    );

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={280}
      shadow="sm"
      position="bottom-end"
      withArrow
      closeOnClickOutside
    >
      <Popover.Target>
        <Tooltip label="Show / hide columns">
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Show or hide columns"
            onClick={() => setOpened((o) => !o)}
          >
            <IconColumns
              size={AppConstants.ICON_SIZE}
              stroke={AppConstants.ICON_STROKE_WIDTH}
            />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={600}>
            Columns
          </Text>
          <Text size="xs" c="dimmed">
            {visibleCount} of {columns.length} shown
          </Text>
        </Group>
        <TextInput
          size="xs"
          placeholder="Find a column"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          mb="xs"
        />
        <ScrollArea.Autosize mah={320} type="auto" offsetScrollbars>
          <Stack>
            {shown.map((col) => {
              const id = columnId(col);
              const checked = Boolean(visibility[id]);
              return (
                <Checkbox
                  key={id}
                  // size="xs"
                  label={col.header ?? id}
                  checked={checked}
                  // Keep at least one column on screen (an always-shown
                  // column already does).
                  disabled={checked && visibleCount <= 1 && !hasFixedColumns}
                  onChange={(e) =>
                    onChange({ ...visibility, [id]: e.currentTarget.checked })
                  }
                />
              );
            })}
            {!shown.length ? (
              <Text size="xs" c="dimmed">
                No matching columns
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
        {/* <Divider my="xs" />
        <Group justify="space-between" gap="xs">
          <Button size="sm" variant="subtle" onClick={onReset}>
            Reset to default
          </Button>
          <Group>
            <Button size="sm" variant="subtle" onClick={() => setAll(false)}>
              None
            </Button>
            <Button size="sm" variant="subtle" onClick={() => setAll(true)}>
              All
            </Button>
          </Group>
        </Group> */}
      </Popover.Dropdown>
    </Popover>
  );
};

export default ColumnSelectPopover;
